import im from "immutable";

import { isSelectable } from "~/components/inspectorv2/traverseValue/TypeTagInfo";
import {
	type ValuePath,
	type ValuePathSlice,
	joinPath,
} from "~/components/inspectorv2/traverseValue/ValuePath";

import { type GroupBy, appendGroupedFields } from "./GroupBy";
import { type OrderBy, appendOrderedFields } from "./OrderBy";
import { type QueryNodeType } from "./QueryNodeType";
import { type QueryableField, enumerateQueryableFields } from "./QueryableField";
import { type Select, toggleFieldSelected } from "./Select";
import { type Where, appendWherePredicate } from "./Where";

export type FieldSet = im.Map<string, QueryableField>;

export interface QueryBuilderStatePayload {
	queryingCellId: string;
	pathToQueryRoot: ValuePath;
	fields: FieldSet;
	initialQueryNodeEditing: QueryNodeType | undefined;
	currQueryNodeEditing: QueryNodeType | null;

	selectedFields: Select;
	wherePredicates: Where;
	groupedFields: GroupBy;
	orderedFields: OrderBy;
}
export type QueryBuilderState = im.Record<QueryBuilderStatePayload>;

export const setInitialQueryNodeEditing = (
	s: QueryBuilderState | null,
	queryNodeEditing: QueryNodeType | undefined
): QueryBuilderState | null => {
	if (!s) {
		return s;
	}

	return s.set("initialQueryNodeEditing", queryNodeEditing);
};

export const setCurrQueryNodeEditing = (
	s: QueryBuilderState | null,
	queryNodeEditing: QueryNodeType | null
): QueryBuilderState | null => {
	if (!s) {
		return s;
	}

	return s.set("currQueryNodeEditing", queryNodeEditing);
};

export const emptyQueryBuilderState = (
	cellId: string,
	pathToQueryRoot: ValuePath,
	pathFromQueryRoot: ValuePathSlice,
	queryNodeEditing?: QueryNodeType
): QueryBuilderState => {
	const fields = enumerateQueryableFields(pathToQueryRoot);

	const factory = im.Record<QueryBuilderStatePayload>({
		queryingCellId: cellId,
		pathToQueryRoot,
		fields,
		initialQueryNodeEditing: queryNodeEditing,
		currQueryNodeEditing: queryNodeEditing || null,
		selectedFields: selectableFields(fields),
		wherePredicates: im.List(),
		groupedFields: { groupedFields: im.Map(), groupedFieldOrdering: im.List() },
		orderedFields: { orderedFields: im.Map(), orderedFieldOrdering: im.List() },
	});

	const state = factory();

	const field = fields.get(joinPath(pathFromQueryRoot.slice(1)));
	if (!field) {
		return state;
	}

	switch (queryNodeEditing) {
		case "select":
			return toggleFieldSelected(state, field) || state;
		case "where":
			return appendWherePredicate(state, field) || state;
		case "orderby":
			return appendOrderedFields(state, [field]) || state;
		case "groupby":
			return appendGroupedFields(state, [field]) || state;
		default:
			return state;
	}
};

const selectableFields = (fields: FieldSet) =>
	fields.filter(({ typeInfo }) => isSelectable(typeInfo));
